import { useRef, useCallback } from "react";

const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

export const pickAudioMimeType = () => {
  if (typeof MediaRecorder === "undefined") {
    return null;
  }
  for (const type of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return "";
};

export const buildAudioFilename = (mimeType, ticketChannel) => {
  const ts = Date.now();
  const isWhatsApp = ["whatsapp", "whatsapp_oficial"].includes(ticketChannel);

  if (isWhatsApp) {
    return `audio_${ts}.mp3`;
  }

  const normalized = (mimeType || "").toLowerCase();
  if (normalized.includes("mpeg") || normalized.includes("mp3")) {
    return `${ts}.m4a`;
  }
  if (normalized.includes("mp4")) {
    return `${ts}.m4a`;
  }
  return `${ts}.m4a`;
};

export const resolveAudioFileType = (mimeType) => mimeType || "audio/mpeg";

/**
 * Gravação via MediaRecorder nativo — sem encode MP3 em tempo real na main thread.
 */
const useTicketAudioRecorder = () => {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (typeof MediaRecorder === "undefined") {
      throw new Error("MediaRecorder não suportado neste navegador");
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });

    streamRef.current = stream;
    chunksRef.current = [];

    const mimeType = pickAudioMimeType();
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    );
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    await new Promise((resolve, reject) => {
      recorder.onerror = (event) => {
        reject(event.error || new Error("Erro na gravação de áudio"));
      };
      recorder.start(250);
      resolve();
    });
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        cleanupStream();
        reject(new Error("Nenhuma gravação ativa"));
        return;
      }

      recorder.onstop = () => {
        try {
          const mimeType =
            recorder.mimeType ||
            chunksRef.current[0]?.type ||
            pickAudioMimeType() ||
            "audio/webm";
          const blob = new Blob(chunksRef.current, { type: mimeType });
          chunksRef.current = [];
          mediaRecorderRef.current = null;
          cleanupStream();

          if (blob.size < 1000) {
            reject(new Error("Áudio muito curto"));
            return;
          }

          resolve({ blob, mimeType });
        } catch (error) {
          reject(error);
        }
      };

      recorder.stop();
    });
  }, [cleanupStream]);

  const cancelRecording = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        cleanupStream();
        resolve();
        return;
      }

      recorder.onstop = () => {
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        cleanupStream();
        resolve();
      };
      recorder.stop();
    });
  }, [cleanupStream]);

  return {
    startRecording,
    stopRecording,
    cancelRecording,
    isMediaRecorderSupported: typeof MediaRecorder !== "undefined",
  };
};

export default useTicketAudioRecorder;
